import React, { FocusEventHandler } from 'react';
import classnames from 'classnames';
import ReactSelect, {
	ActionMeta,
	MenuPosition,
	OnChangeValue,
	GroupBase,
	Props as ReactSelectProps,
	components,
	MenuListProps,
} from 'react-select';
import Creatable from 'react-select/creatable';

import { Label, LabelProps } from '../Label';
import { Errors } from '../Errors';
import { getErrorData, ReduxFieldInputTypes, ReduxFieldMetaTypes, ShowErrorsOn } from '../utils';
import commonStyles from '../common.scss';
import css from './Select.scss';

export const optionByValue = (
	options: readonly Option[] | GroupBase<Option>[],
	value?: string | number | Array<Option>
) => {
	if (Array.isArray(value)) {
		return value;
	}

	for (const option of options) {
		if (typeof option === 'object' && 'label' in option && 'options' in option) {
			const result = optionByValue(option.options, value);
			if (result) return result;
		}
		if ((option as Option).value === value) {
			return option;
		}
	}

	return null;
};

export type Option = {
	value?: string | number;
	label?: React.ReactNode;
	// creatable
	__isNew__?: boolean;
};

export type SelectProps<O extends Option = Option, M extends boolean = false> = {
	options: O[] | GroupBase<O>[];
	input?: ReduxFieldInputTypes;
	meta?: ReduxFieldMetaTypes;
	placeholder?: string;
	isSearchable?: boolean;
	isCreatable?: boolean;
	isMulti?: M;
	closeMenuOnSelect?: boolean;
	defaultValue?: O;
	value?: any;
	theme?: 'dark' | 'light';
	className?: string;
	label?: LabelProps;
	inline?: boolean;
	menuPosition?: MenuPosition;
	showErrorsOn?: ShowErrorsOn;
	error?: string;
	eventListeners?: {
		onChange?: (
			value: M extends true ? OnChangeValue<O, true> : Exclude<OnChangeValue<O, false>, null>['value'],
			action: ActionMeta<unknown>['action'],
			name?: ActionMeta<unknown>['name']
		) => void;
		onBlur?: FocusEventHandler<HTMLInputElement>;
		onFocus?: FocusEventHandler<HTMLInputElement>;
	};
	renderMenuAfterComponents?: () => React.ReactNode;
	disabled?: boolean;
} & Partial<Pick<ReactSelectProps, 'menuPlacement' | 'maxMenuHeight' | 'noOptionsMessage' | 'isClearable'>>;

export function Select<O extends Option = Option, M extends boolean = false>(props: SelectProps<O, M>) {
	const {
		input,
		options,
		meta,
		isMulti,
		className = '',
		label = { component: 'div' },
		inline = false,
		theme = 'light',
		menuPosition = 'absolute',
		showErrorsOn = 'touched',
		error = '',
		closeMenuOnSelect = true,
		isSearchable = false,
		isCreatable = false,
		placeholder = 'Select...',
		eventListeners = {},
		renderMenuAfterComponents = null,
		noOptionsMessage = undefined,
		disabled = false,
		...rest
	} = props;
	const [inputValue, setInputValue] = React.useState('');

	const menuRef = React.useRef<HTMLDivElement | null>(null);

	const [createdOptions, setCreatedOptions] = React.useState<Option[]>(() => {
		if (!isCreatable) return [];

		const currentValue = input?.value ?? props.value;
		if (!currentValue) return [];

		const existsInOptions = options.some(option => option.value === currentValue);
		if (existsInOptions) return [];

		return [
			{
				value: currentValue,
				label: currentValue,
				__isNew__: true,
			},
		];
	});
	const id = label?.htmlFor ? label.htmlFor : meta && input ? `${meta.form}.${input.name}` : '';
	const errorData = getErrorData({ meta, input, error, showErrorsOn });

	const onBlur: React.FocusEventHandler<HTMLInputElement> = event => {
		eventListeners?.onBlur?.(event);
	};

	const onFocus: React.FocusEventHandler<HTMLInputElement> = event => {
		input?.onFocus(input.value);
		eventListeners?.onFocus?.(event);
	};

	const handleInputChange = (value: string) => {
		setInputValue(value);
	};

	const onChange = (option: OnChangeValue<O, false> | OnChangeValue<O, true>, { action, name }: ActionMeta<O>) => {
		const nextValue = Array.isArray(option)
			? (option as OnChangeValue<O, true>)
			: (option as OnChangeValue<O, false>)?.value;

		if (isCreatable && action === 'create-option' && option) {
			setCreatedOptions(prev => [...prev, ...(Array.isArray(option) ? [...option] : [option])]);
		}

		input?.onChange(nextValue);

		eventListeners?.onChange?.(
			nextValue as M extends true ? OnChangeValue<O, true> : Exclude<OnChangeValue<O, false>, null>['value'],
			action,
			name
		);
	};

	const closeMenuOnScroll = React.useMemo(() => {
		return menuPosition === 'fixed'
			? (event: Event) => {
					const { target } = event;
					if (!(target instanceof Node)) return false;
					if (menuRef.current?.contains(target)) return false;
					return true;
				}
			: false;
	}, [menuPosition]);

	const componentsProp = React.useMemo(() => {
		return {
			MenuList: (_props: MenuListProps<O>) => {
				return (
					<components.MenuList {..._props} innerRef={menuRef}>
						{_props.children}
						{renderMenuAfterComponents?.()}
					</components.MenuList>
				);
			},
		};
	}, [renderMenuAfterComponents]);

	return (
		<div
			className={classnames(css.field, css.fieldText, className, css[theme], {
				[css.inline]: inline,
				[css.error]: errorData.show,
				[commonStyles.disabled]: disabled,
			})}
		>
			<Label className={css.label} {...label} component={id ? 'label' : 'div'} htmlFor={id} />
			<div className={css.fieldInner}>
				{isCreatable ? (
					<Creatable
						closeMenuOnScroll={closeMenuOnScroll}
						{...input}
						{...rest}
						isDisabled={disabled}
						isMulti={!!isMulti}
						closeMenuOnSelect={closeMenuOnSelect}
						inputValue={inputValue}
						className={css.select}
						classNamePrefix="creatable"
						inputId={id}
						placeholder={placeholder}
						value={optionByValue(
							[...options, ...createdOptions],
							input?.value ?? props.value ?? rest.defaultValue?.value
						)}
						options={options}
						onFocus={onFocus}
						menuPosition={menuPosition}
						onInputChange={handleInputChange}
						onBlur={onBlur}
						onChange={onChange}
						noOptionsMessage={noOptionsMessage}
						components={componentsProp}
					/>
				) : (
					<ReactSelect
						closeMenuOnScroll={closeMenuOnScroll}
						{...input}
						{...rest}
						isDisabled={disabled}
						className={css.select}
						classNamePrefix="select"
						isSearchable={isSearchable}
						isMulti={isMulti}
						closeMenuOnSelect={closeMenuOnSelect}
						inputId={id}
						placeholder={placeholder}
						value={optionByValue(options, input?.value ?? props.value ?? rest.defaultValue?.value)}
						options={options}
						onFocus={onFocus}
						menuPosition={menuPosition}
						onBlur={onBlur}
						onChange={onChange}
						noOptionsMessage={noOptionsMessage}
						components={componentsProp}
					/>
				)}
			</div>
			<Errors className={css.errors} show={errorData.show}>
				{errorData.message}
			</Errors>
		</div>
	);
}

/* Example:

<Field
	name="username"
	component={Select}
	validate={[required, lettersOnly]}
    // validate array recommended to create out of render method to avoid wasted renders
	className="--custom-class"
	label={{ children: 'username', component: 'h1', className: ''--custom-label-class }}
	limit={{ max: 50, className: ''--custom-limit-class }}
	reset
	placeholder="username"
	showErrorsOn={false}
	autoSelect
	inline
/>
*/
